import React from 'react';
import {
  CheckOutlined,
  LockOutlined,
  ThunderboltFilled,
  ExperimentOutlined,
  StarFilled,
  ClockCircleFilled,
} from '@ant-design/icons';

import { NodeStatus, PlanNode } from '../types';
import { TIER_LABEL } from '../helpers';

type Props = {
  node: PlanNode;
  status: NodeStatus;
  color: string;
  selected: boolean;
  faded: boolean;
  inProgress?: boolean;
  onSelect: (id: string) => void;
  onHover: (id: string | null) => void;
};

/** A single skill node in the tree. Position is applied by the parent. */
const SkillNodeCard: React.FC<Props> = ({
  node,
  status,
  color,
  selected,
  faded,
  inProgress = false,
  onSelect,
  onHover,
}) => {
  const stateClass = status.unlocked
    ? 'is-unlocked'
    : status.canUnlock
      ? 'is-available'
      : 'is-locked';

  const StatusIcon = status.unlocked
    ? CheckOutlined
    : status.canUnlock
      ? ThunderboltFilled
      : LockOutlined;

  return (
    <button
      type="button"
      className={[
        'devplan-node',
        stateClass,
        // Mock recommendation emphasis. `node.recommended` is false for every trainee the
        // feature is off for, so this adds nothing by default.
        node.recommended ? 'is-recommended' : '',
        inProgress ? 'is-in-progress' : '',
        selected ? 'is-selected' : '',
        faded ? 'is-faded' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={{ ['--node-color' as string]: color }}
      // Stop the canvas pan from starting when interacting with a node.
      onMouseDown={e => e.stopPropagation()}
      onClick={e => {
        e.stopPropagation();
        onSelect(node.code);
      }}
      onMouseEnter={() => onHover(node.code)}
      onMouseLeave={() => onHover(null)}
    >
      <span className="devplan-node__head">
        <span className="devplan-node__tier">{TIER_LABEL[node.tier]}</span>
        {node.recommended && (
          <span className="devplan-node__reco" title="Recommended for you">
            <StarFilled /> Recommended
          </span>
        )}
        {inProgress && (
          <span
            className="devplan-node__progress"
            title="Single player session in progress"
          >
            <ClockCircleFilled /> In progress
          </span>
        )}
        <span className="devplan-node__status-icon">
          <StatusIcon />
        </span>
      </span>

      <span className="devplan-node__name">{node.name}</span>

      <span className="devplan-node__foot">
        <span className="devplan-node__pips" aria-hidden>
          {Array.from({ length: node.max_rank }).map((_, i) => (
            <span
              key={i}
              className={
                i < node.rank ? 'devplan-pip is-filled' : 'devplan-pip'
              }
            />
          ))}
        </span>
        {node.game_count > 0 && (
          <span className="devplan-node__scen" title="Practice games">
            <ExperimentOutlined /> {node.game_count}
          </span>
        )}
      </span>
    </button>
  );
};

export default SkillNodeCard;
